import { Box, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { SortableColumnLabel, sortIndicatorSx } from "components";
import { useMemo, useState } from "react";
import type { AskHelenaTicketRef, ColumnSort } from "shared";
import { answerLinkSx } from "../utils/answerLinkStyles";
import type { MarkdownTableSegment } from "../utils/markdownSegments";
import { markdownCellText } from "../utils/markdownSegments";
import { markdownTableSx } from "../utils/markdownTableStyles";
import {
  inlineMarkdownToHtml,
  MARKDOWN_TABLE_SCROLL_CLASS,
} from "../utils/markdownToHtml";
import { sortMarkdownTableRows } from "../utils/sortMarkdownTableRows";

const NEXT_SORT: Record<ColumnSort, ColumnSort> = {
  natural: "asc",
  asc: "desc",
  desc: "natural",
};

const ARIA_SORT: Record<ColumnSort, "none" | "ascending" | "descending"> = {
  natural: "none",
  asc: "ascending",
  desc: "descending",
};

// The active-sort icons nest a 20x20 <svg> inside SvgIcon's 24x24 viewport, so
// the glyph lands in the top-left of the icon box and reads as sitting above
// the heading. Filling the viewport re-centres it against the label.
const SORT_ICON_SX: SystemStyleObject<Theme> = {
  "& th .MuiSvgIcon-root > svg": { width: "100%", height: "100%" },
};

interface AskHelenaAnswerTableProps {
  table: MarkdownTableSegment;
  ticketRefs?: AskHelenaTicketRef[] | null;
}

/**
 * A GFM table from a Helena answer, sortable by column.
 *
 * Sorting is client-side over the rows already received — a tool result caps a
 * ticket list at 25 rows, so this orders the rows on screen, not the whole
 * result set (the answer text states the total).
 */
export const AskHelenaAnswerTable = ({
  table,
  ticketRefs,
}: AskHelenaAnswerTableProps) => {
  const theme = useTheme();
  const [sortColumn, setSortColumn] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<ColumnSort>("natural");

  const headers = useMemo(
    () =>
      table.header.map((cell, index) => ({
        id: `header-${index}`,
        label: markdownCellText(cell),
        index,
      })),
    [table.header],
  );

  const rows = useMemo(
    () =>
      sortColumn === null
        ? table.rows
        : sortMarkdownTableRows(table.rows, sortColumn, sortDirection),
    [table.rows, sortColumn, sortDirection],
  );

  const renderedRows = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        cells: row.cells.map((cell, index) => ({
          id: `${row.id}-${index}`,
          html: inlineMarkdownToHtml(cell, ticketRefs),
        })),
      })),
    [rows, ticketRefs],
  );

  // Column alignment comes from the Markdown delimiter row, so it can only be
  // expressed positionally — `nth-of-type` also outranks the `& th, & td`
  // default that `markdownTableSx` sets.
  const sx = useMemo(() => {
    const alignment: Record<string, SystemStyleObject<Theme>> = {};

    table.align.forEach((align, index) => {
      if (!align || align === "left") return;
      const column = index + 1;
      alignment[`& th:nth-of-type(${column}), & td:nth-of-type(${column})`] = {
        textAlign: align,
      };
    });

    return {
      ...markdownTableSx(theme),
      ...answerLinkSx(theme),
      ...SORT_ICON_SX,
      ...alignment,
    };
  }, [theme, table.align]);

  const columnSort = (index: number): ColumnSort =>
    index === sortColumn ? sortDirection : "natural";

  const toggleSort = (index: number) => {
    if (index !== sortColumn) {
      setSortColumn(index);
      setSortDirection("asc");
      return;
    }

    const next = NEXT_SORT[sortDirection];
    setSortDirection(next);
    if (next === "natural") setSortColumn(null);
  };

  if (headers.length === 0) return null;

  return (
    <Box sx={sx}>
      <Box className={MARKDOWN_TABLE_SCROLL_CLASS}>
        <Box component="table">
          <Box component="thead">
            <Box component="tr">
              {headers.map((header) => (
                <Box
                  component="th"
                  key={header.id}
                  scope="col"
                  aria-sort={ARIA_SORT[columnSort(header.index)]}
                  sx={sortIndicatorSx(columnSort(header.index))}
                >
                  <SortableColumnLabel
                    label={header.label}
                    sort={columnSort(header.index)}
                    onToggle={() => toggleSort(header.index)}
                    isEmpty={rows.length === 0}
                  />
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {renderedRows.map((row) => (
              <Box component="tr" key={row.id}>
                {row.cells.map((cell) => (
                  <Box
                    component="td"
                    key={cell.id}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized in inlineMarkdownToHtml
                    dangerouslySetInnerHTML={{ __html: cell.html }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
